import type { RewardsReservation, RewardsReservationStatus } from '~/services/LuxLoyalty';

/** One row of the Flash Rewards reservations table. */
export interface FlashReservationRow {
  id: string;
  orderId: string;
  /** The offer the reservation's pool belongs to; null when the pool isn't in the loaded list. */
  offerId: string | null;
  poolId: string | null;
  status: RewardsReservationStatus;
  /** Check-in date (YYYY-MM-DD) from the reservation's stay details. */
  checkIn: string | null;
  nights: number | null;
  points: number;
  cash: number;
  currency: string;
  reservedAt: string;
  expiresAt: string;
}

/**
 * The reservation fields this table reads. `poolId` and `details` are in loyalty's live response
 * but not yet in the published contract this app types against, so they're declared here as
 * optional. TODO(LOYAL-2863): take them from the contract once it's published.
 */
type ReservationInput = Pick<
  RewardsReservation,
  'reservationId' | 'orderId' | 'status' | 'pointsCharged' | 'cashCharged' | 'cashCurrency' | 'reservedAt' | 'expiresAt'
> & {
  poolId?: string | null;
  details?: unknown;
};

/**
 * Builds a table row for a flash-hotel reservation. Reservations carry no offer id, only a
 * `poolId`, so the offer comes from `offerIdByPoolId` (built from the pools list). The stay
 * comes from the untyped `details` blob, so anything missing or of the wrong type becomes null.
 */
export function toFlashReservationRow(
  reservation: ReservationInput,
  offerIdByPoolId: ReadonlyMap<string, string>,
): FlashReservationRow {
  const details =
    typeof reservation.details === 'object' && reservation.details !== null
      ? (reservation.details as Record<string, unknown>)
      : {};
  return {
    id: reservation.reservationId,
    orderId: reservation.orderId,
    offerId: reservation.poolId ? (offerIdByPoolId.get(reservation.poolId) ?? null) : null,
    poolId: reservation.poolId ?? null,
    status: reservation.status,
    checkIn: typeof details.checkIn === 'string' ? details.checkIn : null,
    nights: typeof details.nights === 'number' ? details.nights : null,
    points: reservation.pointsCharged,
    cash: reservation.cashCharged,
    currency: reservation.cashCurrency,
    reservedAt: reservation.reservedAt,
    expiresAt: reservation.expiresAt,
  };
}
